import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  AfterViewChecked,
  booleanAttribute,
  Component,
  DestroyRef,
  EventEmitter,
  HostBinding,
  input,
  Input,
  OnChanges,
  OnInit,
  output,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, skipWhile } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { GalaxyTableSearchService } from '../../services/table-search.service';
import { GalaxyTableSelectionService } from '../../services/table-selection.service';

const SEARCH_TERM_DELAY = 300; // ms

@Component({
  selector: 'glxy-table-content-header',
  templateUrl: './table-content-header.component.html',
  styleUrls: ['./table-content-header.component.scss'],
  providers: [],
  animations: [
    trigger('openClose', [
      state(
        'open',
        style({
          height: '*',
          opacity: 1,
        }),
      ),
      state(
        'closed',
        style({
          height: '0px',
          opacity: 0,
        }),
      ),
      transition('open <=> closed', [animate('0.2s')]),
    ]),
  ],
  standalone: false,
})
export class TableContentHeaderComponent implements OnInit, OnChanges, AfterViewChecked {
  @HostBinding('class') class = 'glxy-table-content-header';

  @Input({ transform: booleanAttribute }) showColumnArrange?: boolean = true;
  @Input() columnArrangeType?: 'simple' | 'advanced' = 'simple';
  @Input({ transform: booleanAttribute }) showFiltersButton?: boolean = true;
  @Input({ transform: booleanAttribute }) showFilters?: boolean = true;
  @Input({ transform: booleanAttribute }) showFiltersOpen?: boolean = true;
  @Output() showFiltersOpenChange = new EventEmitter<boolean>();
  @Input({ transform: booleanAttribute }) showFiltersApplied?: boolean = false;
  @Input({ transform: booleanAttribute }) showSearch?: boolean = true;
  @Input({ transform: booleanAttribute }) showSort?: boolean = true;
  @Input({ transform: booleanAttribute }) showExport?: boolean = false;
  @Input({ transform: booleanAttribute }) showDisplayToggle?: boolean = false;
  @Input({ transform: booleanAttribute }) showActions?: boolean = true;
  @Input({ transform: booleanAttribute }) saveSearchToQueryParams?: boolean = false;
  @Input({ transform: booleanAttribute }) showSelectedAllCount?: boolean = false;
  // overrides the search value with NgOnChanges
  @Input() emitSearchChange?: string;
  @Input() hideActionButton = false;

  @Output() export = new EventEmitter<void>();
  searchControl: UntypedFormControl = new UntypedFormControl();
  numSelected$?: Observable<number>;
  display = input<'grid' | 'list'>('list');
  displayChanges = output<'grid' | 'list'>();
  rowsSelectionLimit = this.selectionService.rowsSelectionLimit;

  private initialSearchTerm = '';
  private initialSearchTermSet = false;

  constructor(
    private searchService: GalaxyTableSearchService,
    private selectionService: GalaxyTableSelectionService,
    private destroyRef: DestroyRef,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
  ) {}

  ngAfterViewChecked(): void {
    if (this.initialSearchTerm && !this.initialSearchTermSet) {
      this.searchControl.setValue(this.initialSearchTerm);
      this.searchControl.updateValueAndValidity();
      this.initialSearchTermSet = true;
    }
  }

  ngOnInit(): void {
    if (this.saveSearchToQueryParams) {
      const searchParam = this.activatedRoute.snapshot.queryParamMap.get('search');
      if (searchParam) {
        this.initialSearchTerm = decodeURI(searchParam);
      }
    }

    // When the user enters a search term, update and emit.
    this.searchControl.valueChanges
      .pipe(
        skipWhile((term) => term !== this.initialSearchTerm),
        debounceTime(SEARCH_TERM_DELAY),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (text: string) => {
          this.setSearchTerm(text);
        },
      });
    // When the search term is programmatically updated, update without emitting.
    this.searchService.searchTerm$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (searchTerm) => {
        this.searchControl.setValue(searchTerm, { emitEvent: false });
        this.searchControl.updateValueAndValidity();
      },
    });

    this.numSelected$ = this.selectionService.selection$.pipe(map((selection) => selection.length));
  }

  ngOnChanges() {
    if (this.emitSearchChange) {
      this.searchControl.setValue(this.emitSearchChange);
      this.searchControl.updateValueAndValidity();
    }
  }

  setSearchTerm(searchTerm: string): void {
    if (this.saveSearchToQueryParams) {
      let searchParam: string | null = null;
      if (searchTerm) {
        searchParam = encodeURI(searchTerm);
      }
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { search: searchParam },
        queryParamsHandling: 'merge',
      });
    }
    this.searchService.updateTerm(searchTerm);
  }

  toggleFilterBar(): void {
    this.showFiltersOpen = !this.showFiltersOpen;
    this.showFiltersOpenChange.emit(this.showFiltersOpen);
  }

  onChangeDisplayOption(value: 'grid' | 'list'): void {
    this.displayChanges.emit(value);
  }
}
